import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, Sparkles, Building2, Rocket } from 'lucide-react';
import { FadeUpOnScroll, ScaleOnScroll } from './ScrollReveal';
import { GradientText } from './AnimatedText';
import { TiltCard } from './TiltCard';
import { ConfettiButton } from './ConfettiButton';
import { AnimatedBorder } from './FloatingElements';

const plans = [
  {
    name: 'Starter',
    icon: Rocket,
    price: 49,
    period: '/Monat',
    description: 'Perfekt für kleine Cafés und Imbisse',
    features: [
      '1 Kassenstation',
      'TSE-Zertifizierung inkl.',
      'Unbegrenzte Transaktionen',
      'Basis-Berichte',
      'E-Mail Support',
      'Monatlich kündbar'
    ],
    cta: 'Jetzt starten',
    popular: false
  },
  {
    name: 'Professional',
    icon: Sparkles,
    price: 99,
    period: '/Monat',
    description: 'Für wachsende Restaurants und Bars',
    features: [
      'Bis zu 5 Stationen',
      'TSE-Zertifizierung inkl.',
      'Küchendisplay-System',
      'Lieferservice-Modul',
      'DATEV-Export',
      'Portal-Integration',
      'Erweiterte Berichte',
      'Prioritäts-Support',
      'KI-Menü-Import'
    ],
    cta: 'Beliebteste Wahl',
    popular: true
  },
  {
    name: 'Enterprise',
    icon: Building2,
    price: 'Individuell',
    period: '',
    description: 'Für Ketten und große Betriebe',
    features: [
      'Unbegrenzte Stationen',
      'Multi-Standort-Management',
      'Dedizierter Account Manager',
      'Individuelle Integrationen',
      'SLA-Garantie',
      'Vor-Ort-Schulung',
      'White-Label Option',
      '24/7 Premium Support'
    ],
    cta: 'Kontakt aufnehmen',
    popular: false
  }
];

export const PricingSectionUltimate = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: '#080d18' }}
    >
      {/* Background */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(47,128,255,0.08), transparent)'
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <FadeUpOnScroll className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-emerald-400 text-sm font-medium">14 Tage kostenlos testen</span>
          </div>
          <h2 
            data-testid="pricing-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Transparente <GradientText from="#2DD4BF" to="#2F80FF">Preise</GradientText>
          </h2>
          <p className="mt-6 text-lg sm:text-xl max-w-2xl mx-auto text-slate-400">
            Keine versteckten Kosten. TSE-Gebühren bereits inklusive.
          </p>
        </FadeUpOnScroll>

        {/* Billing Toggle */}
        <FadeUpOnScroll delay={0.1} className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-3 bg-[#111C32] border border-slate-700/50 rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-blue-500 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Monatlich
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly' 
                  ? 'bg-blue-500 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Jährlich
              <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded-full">
                -20%
              </span>
            </button>
          </div>
        </FadeUpOnScroll>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, i) => (
            <ScaleOnScroll key={plan.name} delay={i * 0.15}>
              {plan.popular ? (
                <AnimatedBorder>
                  <PricingCard plan={plan} billingCycle={billingCycle} />
                </AnimatedBorder>
              ) : (
                <TiltCard tiltAmount={5}>
                  <PricingCard plan={plan} billingCycle={billingCycle} />
                </TiltCard>
              )}
            </ScaleOnScroll>
          ))}
        </div>

        {/* Trust Note */}
        <FadeUpOnScroll delay={0.5} className="text-center mt-16">
          <p className="text-slate-500 text-sm">
            Alle Preise zzgl. MwSt. • Monatlich kündbar • Keine Einrichtungsgebühr
          </p>
        </FadeUpOnScroll>
      </div>
    </section>
  );
};

const PricingCard = ({ plan, billingCycle }) => {
  const yearlyDiscount = 0.8;
  const displayPrice = typeof plan.price === 'number' 
    ? (billingCycle === 'yearly' ? Math.round(plan.price * yearlyDiscount) : plan.price)
    : plan.price;

  return (
    <div
      data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
      className={`relative p-8 rounded-2xl h-full flex flex-col ${
        plan.popular 
          ? 'bg-[#111C32]' 
          : 'bg-[#111C32]/80 border border-slate-700/50'
      }`}
    >
      {/* Popular Badge */}
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full">
            Beliebteste Wahl
          </span>
        </div>
      )}

      {/* Icon & Name */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          plan.popular ? 'bg-blue-500/20' : 'bg-slate-700/50'
        }`}>
          <plan.icon className={`h-6 w-6 ${
            plan.popular ? 'text-blue-400' : 'text-slate-400'
          }`} />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {plan.name}
          </h3>
        </div>
      </div>

      {/* Price */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          {typeof displayPrice === 'number' ? (
            <>
              <span className="text-4xl font-bold text-white">€{displayPrice}</span>
              <span className="text-slate-400">{plan.period}</span>
            </>
          ) : (
            <span className="text-3xl font-bold text-white">{displayPrice}</span>
          )}
        </div>
        <p className="mt-2 text-sm text-slate-400">{plan.description}</p>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-grow">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
            <span className="text-sm text-slate-300">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {plan.popular ? (
        <ConfettiButton
          data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
          className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white h-12 font-semibold rounded-xl border-0"
        >
          {plan.cta}
          <ArrowRight className="ml-2 h-4 w-4" />
        </ConfettiButton>
      ) : (
        <Button
          data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
          variant="outline"
          className="w-full border-slate-600 text-slate-200 hover:bg-white/5 h-12 font-semibold rounded-xl"
        >
          {plan.cta}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default PricingSectionUltimate;
